"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronDown } from "lucide-react"
import ReactCountryFlag from "react-country-flag"

// Country codes here (not currency codes) purely to look up a flag icon —
// EUR has no ISO country of its own, so it maps to the "EU" flag.
const CURRENCIES = [
  { code: "GBP", symbol: "£", name: "British Pound", flagCountry: "GB" },
  { code: "USD", symbol: "$", name: "US Dollar", flagCountry: "US" },
  { code: "EUR", symbol: "€", name: "Euro", flagCountry: "EU" },
  { code: "CAD", symbol: "$", name: "Canadian Dollar", flagCountry: "CA" },
  { code: "AUD", symbol: "$", name: "Australian Dollar", flagCountry: "AU" },
  { code: "INR", symbol: "₹", name: "Indian Rupee", flagCountry: "IN" },
]

const COUNTRIES = [
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "IN", name: "India" },
  { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" },
  { code: "SE", name: "Sweden" },
  { code: "DK", name: "Denmark" },
]

// Unicode flag emoji render as plain letters (or blank boxes) on systems
// without a color-emoji font — common on Windows and many Linux desktops.
// Real flag icons (SVG, via the CDN react-country-flag already uses
// elsewhere in this codebase, e.g. country-select) render identically
// everywhere.
const Flag = ({ countryCode }: { countryCode: string }) => (
  // @ts-ignore — svg mode's props aren't typed on the emoji-mode overload
  <ReactCountryFlag
    svg
    countryCode={countryCode}
    style={{ width: "16px", height: "16px" }}
  />
)

const STORAGE_KEY_COUNTRY = "ukss_ship_to_country"
const STORAGE_KEY_CURRENCY = "ukss_ship_to_currency"

/**
 * Shipping/currency selector for the top utility bar. The picker's "Save
 * Preferences" button is cosmetic only (does not call any region-switching
 * API) — this is a pre-existing limitation carried over unchanged from the
 * old currency-dropdown, not something this task fixes (see spec §3).
 */
const ShipToSelector = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState(CURRENCIES[0])
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0])
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 })

  // Selection only lives in this component's state, which resets to the
  // COUNTRIES[0]/CURRENCIES[0] default on every mount — so a page refresh
  // always snapped back to GB. Restore whatever was last chosen from
  // localStorage once mounted (can't read it during SSR, so this can't be
  // the initial useState value without causing a hydration mismatch).
  useEffect(() => {
    const storedCountry = localStorage.getItem(STORAGE_KEY_COUNTRY)
    if (storedCountry) {
      const country = COUNTRIES.find((c) => c.code === storedCountry)
      if (country) setSelectedCountry(country)
    }
    const storedCurrency = localStorage.getItem(STORAGE_KEY_CURRENCY)
    if (storedCurrency) {
      const currency = CURRENCIES.find((c) => c.code === storedCurrency)
      if (currency) setSelectedCurrency(currency)
    }
  }, [])

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setPanelPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-white hover:opacity-80 transition-opacity duration-200 cursor-pointer"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="flex items-center gap-1.5">
          Ship to <Flag countryCode={selectedCountry.code} /> {selectedCountry.name}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="fixed w-[460px] bg-white rounded-lg shadow-xl p-6 z-[100000]"
          style={{ top: panelPos.top, right: panelPos.right }}
        >
          <p className="text-sm text-gray-900 font-medium mb-4">
            Select your shipping destination and currency.
          </p>

          <div className="flex flex-col space-y-4">
            <div>
              <label
                htmlFor="shipping-destination"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Shipping Destination
              </label>
              {/* Native <option> elements can't render an <img>/SVG, so the
                  flag lives next to the select instead, mirroring the
                  currently chosen country. */}
              <div className="flex items-center gap-2">
                <Flag countryCode={selectedCountry.code} />
                <select
                  id="shipping-destination"
                  value={selectedCountry.code}
                  onChange={(e) => {
                    const country = COUNTRIES.find((c) => c.code === e.target.value)
                    if (country) {
                      setSelectedCountry(country)
                      localStorage.setItem(STORAGE_KEY_COUNTRY, country.code)
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                >
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="currency-select"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Currency
              </label>
              <div className="flex items-center gap-2">
                <Flag countryCode={selectedCurrency.flagCountry} />
                <select
                  id="currency-select"
                  value={selectedCurrency.code}
                  onChange={(e) => {
                    const currency = CURRENCIES.find((c) => c.code === e.target.value)
                    if (currency) {
                      setSelectedCurrency(currency)
                      localStorage.setItem(STORAGE_KEY_CURRENCY, currency.code)
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} — {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg px-4 py-2.5 text-sm hover:bg-brand-primary-dark transition-colors"
            >
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ShipToSelector
